import { useContext, useEffect, useState } from 'react'
import { Button, Divider } from '@mui/material'
import { useNavigate, useParams } from 'react-router-dom'
import styled from 'styled-components'
import Title from '../custom/Title'
import { TTransitionHistory } from '../../Models/Inbound'
import { DOCUMENT_TYPES, PATHS, TRANSACTION_STATUS, TRANSACTION_TYPES } from '../../utils/constant'
import { TStepsProps } from '../../Models/common'
import { GridColDef } from '@mui/x-data-grid'
import Table from '../custom/Table'
import { TSummaryField } from '../../Models/Summary'
import StepNavigation from '../custom/StepNavigation'
import DownloadInvoice from './DownloadInvoice'
import useService from '../../hooks/useService'
import { fetchAnnexure, fetchDebitNote, fetchJobworkInventory, fetchTransactionDocumentsList } from '../../services/Transactions'
import Modal from '../Modals'
import TransferMetal from './TransferMetalModal'
import { isEmpty } from '../../utils/helpers'
import DeliveryModal from './DeliveryModal'
import { Description } from '@mui/icons-material'
import { generateIRN } from '../../services/Inventory'
import { NotificationContext } from '../../contexts/NotificationContext'
import { CustomerContext } from '../../contexts/CustomerContext'

type TSummaryProps = TStepsProps & {
    summaryDetails: TSummaryField[],
    transitionHistory: TTransitionHistory[]
}

const TRANSITION_DETAILS_FIELDS = [
    {
        id: "id",
        label: "Transition Type"
    },
    {
        id: "globalTransactionNumber",
        label: "Transaction Number"
    }, {
        id: "toStatus",
        label: "Transition Status"
    }
]

const List = styled.div`
    display: flex;
    gap:10px;
    flex-wrap:wrap;
    padding-bottom:10px;
`
const Item = styled.div`
    display: flex;
    padding: 10px;
    flex-direction: column;
    background: #f1f1f1;
    min-width: 250px;
    width: calc(25% - 10px);
    border-radius: 4px;
`

const ItemLabel = styled.span`
    font-size: 18px;
    font-weight: 600;
`

const ItemValue = styled.span`
    font-size: 16px;
`

const Summary = (props: TSummaryProps) => {

    const { prevSection, path, summaryDetails, transitionHistory } = props

    const navigate = useNavigate()
    const { id } = useParams()
    const { notify } = useContext(NotificationContext);
    const { customerConfig } = useContext(CustomerContext);
    const { execute, store: { transactionsStore } } = useService()
    const { capturedSteps, transactionInfo, jobworkInventory, transactionDocuments } = transactionsStore
    const [defaultSummaryDetails, setDefaultSummaryDetails] = useState<TSummaryField[]>([]);
    const [defaultTransitionHistory, setDefaultTransitionHistory] = useState<TTransitionHistory[]>([]);
    const [showTransferMetalModal, setShowTransferMetalModal] = useState(false);
    const [showDeliveryModal, setShowDeliveryModal] = useState(false);
    useEffect(() => {
        if (summaryDetails?.length) {
            setDefaultSummaryDetails(summaryDetails)
        }
        if (transitionHistory?.length) {
            setDefaultTransitionHistory(transitionHistory)
        }

    }, [summaryDetails, transitionHistory])

    const getTransitionDocumentsList = () => {
         execute(()=> fetchTransactionDocumentsList({
            globalTransactionNumber: transactionInfo.globalTransactionNumber!,
            transactionType: transactionInfo.transactionType
        }))
    }
    
    const redirectToIncompleteStep = () => {
        if (prevSection && !capturedSteps.includes(prevSection)) {
            navigate(path + '?section=' + prevSection)
        }
    }

    const handleClose = () => {
        navigate(PATHS.CREATE_TRANSACTION)
    }


    useEffect(() => {
        redirectToIncompleteStep()
        getTransitionDocumentsList()
    }, [])

    const getTransitionDetailsColumns = (): GridColDef<any>[] => {
        let minWidth = 380
        return TRANSITION_DETAILS_FIELDS.map(transitionDetail => {
            return {
                field: transitionDetail.id,
                headerName: transitionDetail.label,
                minWidth
            }
        })
    }
    const goToPrevSection = () => {
        if (id) navigate(path + '/' + id + '?section=' + prevSection)
        else navigate(path + '?section=' + prevSection)
    }

    useEffect(() => {
        if (transactionInfo.transactionType === TRANSACTION_TYPES.GMS) {
            execute(() => fetchJobworkInventory({ globalTransactionNumber: transactionInfo.globalTransactionNumber! }))
        }
    }, [])

    const DownloadAnnexure =async()=>{
        if (transactionInfo.globalTransactionNumber) {
            const { data } = await execute(() => fetchAnnexure({ transactionType: transactionInfo.transactionType, globalTransactionNumber: transactionInfo.globalTransactionNumber!  }))
            
            //@ts-ignore
            const uint8Array = new Uint8Array(data?.data)
            const blob = new Blob([uint8Array], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `ANNEXURE_${transactionInfo.globalTransactionNumber}.xlsx`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        }
    }
    
    const DownloadDebitNote =async()=>{
        if (transactionInfo.globalTransactionNumber && customerConfig?.mtsIsTDSApplicable ==null) {
            let pdfPath = ''
            const { data } = await execute(() => fetchDebitNote({ transactionType: transactionInfo.transactionType, globalTransactionNumber: transactionInfo.globalTransactionNumber!  }))
            
            if (data === null)  return
            pdfPath = data.url
            var link = document.createElement('a');
            link.href = pdfPath;
            link.download = `DEBIT_NOTE_${transactionInfo.globalTransactionNumber}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
    }

    const handleGenerateIRN = async () => {
        const { error } = await execute(() => generateIRN({ GTNumber: transactionInfo.globalTransactionNumber!, type: 'TRANSACTION' }))
    
        if(!error) {
          notify('IRN generated successfully')
        }
    }

    const getTransitionDocument = transactionDocuments.length 
        ? transactionDocuments.find(doc => doc.type === DOCUMENT_TYPES.TAX_INVOICE) 
        : null

    return (
        <div>
            <div style={{ padding: '20px 10px 10px 0', display: 'flex', justifyContent: 'space-between' }}>
                <Title >Transaction Summary</Title>
                <div>
                    {
                        transactionInfo.transactionType !== TRANSACTION_TYPES.GMS
                            ?
                            <DownloadInvoice
                                fileName={transactionInfo.globalTransactionNumber!}
                                label={transactionInfo.transactionType === TRANSACTION_TYPES.BUY_BACK ? 'Download Purchase Receipt' : 'Download Invoice'}
                                documentType={transactionInfo.transactionType === TRANSACTION_TYPES.BUY_BACK ? 'PURCHASE_RECIEPT' : 'TAX_INVOICE'}
                            />
                            :
                            <></>
                    }
                    {
                        transactionInfo.transactionType === TRANSACTION_TYPES.JOB_WORK ?
                            <>
                                &nbsp;
                                <DownloadInvoice
                                    fileName={transactionInfo.globalTransactionNumber!}
                                    label='Download Delivery Challan'
                                    documentType='DELIVERY_CHALLAN'
                                />
                                <Button size="small" onClick={DownloadAnnexure} variant="outlined" startIcon={<Description />}>
                                {'Download Annexure'}
                                </Button>
                            </>
                            :
                            <></>
                    }
                    {
                        transactionInfo.transactionType === TRANSACTION_TYPES.BUY_SELL ?
                            <>
                                <Button size="small" onClick={DownloadAnnexure} variant="outlined" startIcon={<Description />}>
                                {'Download Annexure'}
                                </Button>
                                <Button size="small" disabled={ customerConfig?.mtsIsTDSApplicable !==null } onClick={DownloadDebitNote} variant="outlined" startIcon={<Description />}>
                                {'Download DebitNote'}
                                </Button>
                            </>
                            :
                            <></>
                    }
                    {
                        transactionInfo.transactionType === TRANSACTION_TYPES.GMS ?
                            <>
                                <DownloadInvoice
                                    fileName={transactionInfo.globalTransactionNumber!}
                                    label='Download GMS Certificate'
                                    documentType='CERTIFICATE'
                                />
                                &nbsp;
                                <DownloadInvoice
                                    fileName={transactionInfo.globalTransactionNumber!}
                                    label='Download GMS Reciept'
                                    documentType='GMS_RECIEPT'
                                />
                            </>
                            :
                            <></>
                    }
                    {
                        transactionInfo.status === TRANSACTION_STATUS.CANCELLED && ![TRANSACTION_TYPES.GMS, TRANSACTION_TYPES.BUY_BACK].includes(transactionInfo.transactionType) && transactionDocuments.filter(doc => doc.type === DOCUMENT_TYPES.CREDIT_NOTE).length ?
                            <>
                                &nbsp;
                                <DownloadInvoice
                                    fileName={transactionInfo.globalTransactionNumber!}
                                    label='Download Credit Note'
                                    documentType='CREDIT_NOTE'
                                />
                            </>
                            :
                            <></>
                    }
                    {
                        (transactionInfo.transactionType === TRANSACTION_TYPES.BUY_SELL || transactionInfo.transactionType === TRANSACTION_TYPES.JOB_WORK || transactionInfo.transactionType === TRANSACTION_TYPES.MINTED_SALES || transactionInfo.transactionType === TRANSACTION_TYPES.PURITY_TESTING) ?
                            <>
                                <Button 
                                    size="small" 
                                    onClick={handleGenerateIRN} 
                                    variant="outlined" 
                                    startIcon={<Description />}
                                    disabled={
                                        transactionInfo.status !== TRANSACTION_STATUS.COMPLETED ||
                                        getTransitionDocument?.type !== DOCUMENT_TYPES.TAX_INVOICE ||
                                        (
                                            getTransitionDocument?.type === DOCUMENT_TYPES.TAX_INVOICE &&
                                            (getTransitionDocument?.raw_data.IRNNumber ?? "").trim() !== ""
                                        )
                                    }
                                >
                                    {'Generate IRN'}
                                </Button>
                            </>
                            :
                            <></>
                    }
                </div>
            </div>
            <Divider />
            {transitionHistory.length > 0 &&
                <div style={{ padding: '20px 0px' }}>
                    <Title style={{ textAlign: 'left', margin: '20px 10px 10px 0', color: '#7f7d76' }}>Transition Details</Title>
                    <Table columns={getTransitionDetailsColumns()} rows={defaultTransitionHistory} />
                </div>
            }

            <List>
                {
                    defaultSummaryDetails.map((field) => {
                        if (field.value !== undefined && field.value !== null) {
                            return <Item>
                                <ItemLabel><span>{field.label}</span></ItemLabel>
                                <ItemValue><span>{field.value}</span></ItemValue>
                            </Item>
                        }
                    })
                }
            </List>
            {
                transactionInfo.transactionType === TRANSACTION_TYPES.GMS && !isEmpty(jobworkInventory) ?
                    <div style={{ marginTop: '20px' }}>
                        <Button variant='contained' onClick={() => setShowTransferMetalModal(true)}>Transfer Metal</Button>
                    </div> :
                    <></>
            }
            {
                transactionInfo.transactionType === TRANSACTION_TYPES.BUY_SELL && transactionInfo.status === TRANSACTION_STATUS.DELIVERY_INITIATED ?
                    <div style={{ marginTop: '20px' }}>
                        <Button variant='contained' onClick={() => setShowDeliveryModal(true)}>Create Delivery</Button>
                    </div> :
                    <></>
            }
            {transactionInfo.transactionType !== TRANSACTION_TYPES.MINTED_SALES &&
                ![TRANSACTION_STATUS.CANCELLED, TRANSACTION_STATUS.PENDING_CANCELLATION].includes(transactionInfo.status) &&
                <div style={{ marginTop: '20px' }}>
                    <StepNavigation
                        capturedSteps={capturedSteps}
                        currentSection='SUMMARY'
                        goToPrevSection={goToPrevSection}
                        prevSection={prevSection}
                    />
                </div>
            }
            <Modal
                onClose={() => setShowTransferMetalModal(false)}
                open={showTransferMetalModal}
                title='Transfer Metal'
            >
                <TransferMetal onSuccess={() => setShowTransferMetalModal(false)} />
            </Modal>
            <DeliveryModal
                open={showDeliveryModal}
                onClose={() => setShowDeliveryModal(false)}
            />
        </div>
    )
}

export default Summary